"use client";

import CustomImage from '@/components/image';
import styles from './_baseline.module.scss';
import { useState } from 'react';
// import Button from '@/components/button';

export default function HcpClinicalInformationBaseline({index = 0}) {
  function handleTootip(e) {
    e.currentTarget?.focus();
  }
  return (
    <section className={`${styles.container} container !mb-12 mobile:!mb-24`}>
      <p className="h1 page-heading">
        {index === 0 && <>Baseline characteristics were representative of the CAH population and highlight challenges of effectively treating CAH<sup>1,8,9,11</sup></>}
        {index === 1 && <>Baseline characteristics were representative of CAH population and highlight challenges of effectively treating CAH<sup>1,10,25</sup></>}
      </p>
      
      <div className={styles.tableWrapper}>
        {index === 0 && <picture>
          <source
            media="(max-width: 767px)"
            srcSet="/images/page/hcp/clinical-information/baseline-table-mobile.svg"
            width={342}
            height={2296}
          />
          <CustomImage 
            src="/images/page/hcp/clinical-information/baseline-table.png" 
            alt="Baseline" 
            width={1272}
            height={1202}
            className="mx-auto"
          />
          <button
            className={`${styles.tooltip} ${styles.tooltip_1} ${styles.tooltip_t1}`}
            onClick={handleTootip}
            data-gtm-event-category="Action"
            data-gtm-event-action="Click"
            data-gtm-event-label="Greater than physiologic replacement for all patients"
          >
            <div className={styles.tooltip_content}>
              Greater than physiologic replacement for all patients<sup>1,5</sup>
            </div>
          </button>
          <button
            className={`${styles.tooltip} ${styles.tooltip_2} ${styles.tooltip_t1}`}
            onClick={handleTootip}
            data-gtm-event-category="Action"
            data-gtm-event-action="Click"
            data-gtm-event-label="Mean androstenedione levels"
          >
            <ul className={styles.tooltip_content}>
              {/* <li>Mean androstenedione levels were<br />
                <strong>~2-3&times;</strong> ULN.<sup>5,7</sup></li>
              <hr /> */}
              <li>~80% with androstenedione &gt;ULN<sup>9</sup></li>
              <hr />
              <li>~20% with androstenedione &le;ULN<sup>9</sup></li>
            </ul>
          </button>
        </picture>}
        {index === 1 && <picture>
          <source
            media="(max-width: 767px)"
            srcSet="/images/page/hcp/clinical-information/baseline-table-mobile-2.svg"
            width={342}
            height={1880}
          />
          <CustomImage 
            src="/images/page/hcp/clinical-information/baseline-table-2.png" 
            alt="Baseline" 
            width={1275} 
            height={1175}
            className="mx-auto"
          />
          <button
            className={`${styles.tooltip} ${styles.tooltip_1} ${styles.tooltip_t2}`}
            onClick={handleTootip}
            data-gtm-event-category="Action"
            data-gtm-event-action="Click"
            data-gtm-event-label="Greater than physiologic replacement for all patients"
          >
            <div className={styles.tooltip_content}>
              Greater than physiologic replacement for all patients<sup>1,6</sup>
            </div>
          </button>
          <button
            className={`${styles.tooltip} ${styles.tooltip_2} ${styles.tooltip_t2}`}
            onClick={handleTootip}
            data-gtm-event-category="Action"
            data-gtm-event-action="Click"
            data-gtm-event-label="Mean androstenedione levels"
          >
            <ul className={styles.tooltip_content}>
              {/* <li>Mean androstenedione levels were <strong>~2-3&times;</strong> ULN.<sup>6,19</sup></li>
              <hr /> */}
              <li>~50% with androstenedione &gt;ULN<sup>9</sup></li>
              <hr />
              <li>~50% with androstenedione &le;ULN<sup>9</sup></li>
            </ul>
          </button>
        </picture>}
      </div>

      {index === 0 && <ul className="footnotes">
        <li><sup className="hang">a</sup><span>Plus-minus values are means ± SD.</span></li>
        <li><sup className="hang">b</sup><span>In hydrocortisone equivalent dose.<sup>9</sup></span></li>
        <li><sup className="hang">c</sup><span>Prednisone, prednisolone, or methylprednisolone.<sup>8</sup></span></li>
        <li>
          <sup className="hang">d</sup>
          <span>
            Data at baseline were missing for the following variables: testosterone (1 female participant in the placebo group)
            and testosterone in male participants at Tanner stages 3-5 (1 participant in the CRENESSITY group and 1 in the placebo group).
            <sup>9</sup>
          </span>
        </li>
        <li>
          <sup className="hang">e</sup>
          <span>
            The baseline ratio at Tanner stages 3 through 5 was not available for 1 participant in the CRENESSITY group and 1 participant in the placebo group. Placebo value for Tanner stage 2 is from a single patient and not expressed as mean ± SD. This variable was not assessed for patients at Tanner stage 1 (prepubertal).<sup>9</sup>
          </span>
        </li>
        <li>
          <sup className="hang">f</sup>
          <span>
            Percentages based on male patients who had ultrasonography at baseline (31 taking CRENESSITY, 15 taking placebo).<sup>9</sup>
          </span>
        </li>
        <li>
          <sup className="hang">g</sup>
          <span>Percentages based on female patients (34 taking CRENESSITY, 16 taking placebo).<sup>12</sup></span>
        </li>
      </ul>}

      {index === 1 && <ul className="footnotes">
        <li><sup className="hang">a</sup><span>Plus-minus values are means ± SD.</span></li>
        <li><sup className="hang">b</sup><span>In hydrocortisone equivalent dose.<sup>10</sup></span></li>
        <li><sup className="hang">c</sup><span>Number of male patients who had ultrasonography at baseline (53 taking CRENESSITY, 27 taking placebo).<sup>10</sup></span></li>
        <li>
          <sup className="hang">d</sup>
          <span>
            Percentage based on female patients of child-bearing potential who were not on hormonal or 
            intrauterine contraception (45 taking CRENESSITY, 21 taking placebo).<sup>25</sup>
          </span>
        </li>
        <li>
          <sup className="hang">e</sup>
          <span>
            Percentages based on female participants (61 taking CRENESSITY, 29 taking placebo).<sup>25</sup>
          </span>
        </li>
      </ul>}

      <div className="hcp-callout">
        {index === 0 && <>
          Baseline characteristics were well balanced between the 2 groups.<sup>9</sup>
        </>}
        {index === 1 && <>
        Baseline characteristics were well balanced between the 2 groups.<sup>10</sup>
      </>}
      </div>
    </section>
  );
}
